import React, { FC, useEffect, useRef, useState } from 'react';
import { ButtonBase } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import classNames from 'classnames';

import Add from '@material-ui/icons/Add';
import Check from '@material-ui/icons/Check';

interface IProps extends WithStyles<typeof styles> {
  selected: boolean;
  disabled?: boolean;
  disableHover?: boolean;
  parentHovered?: boolean;
  onClick?: () => void;
  size?: number;
}

const FONT_SIZE_RATIO = 0.8;

const styles = ({ custom, palette }: Theme) =>
  createStyles({
    '@global @keyframes toggle': {
      '0%': { transform: 'scale(1)' },
      '33%': { transform: 'scale(.5)' },
      '66%': { transform: 'scale(1.3)' },
      '100%': { transform: 'scale(1)' },
    },
    wrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transformStyle: 'preserve-3d',
      borderRadius: '50%',
      transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      '&:after': {
        position: 'absolute',
        borderRadius: '50%',
        content: '""',
        width: 'inherit',
        height: 'inherit',
        backgroundColor: custom.palette.grey3,
        transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        transform: 'scale(0) translateZ(-1px)',
      },
      '&$selected, &$selected:hover, &$selected:focus': {
        backgroundColor: palette.primary.main,
      },
    },
    selected: {},
    hover: {
      '&$wrapper:after': {
        transform: 'scale(1) translateZ(-1px)',
      },
      '& $add': {
        fill: '#B3B3B3',
      },
    },
    icon: {
      transition: 'none',
    },
    add: {
      fill: '#D9D9D9',
    },
    check: {
      fill: custom.palette.white,
    },
    changed: {
      animation: 'toggle 600ms ease-in-out',
    },
  });

const ToggleButton: FC<IProps> = props => {
  const { selected, onClick, classes, parentHovered, size, disableHover, disabled } = props;

  const [changed, setChanged] = useState(false);
  const [hovered, setHovered] = useState(false);
  const hasMountRan = useRef(false);

  useEffect(() => {
    if (!hasMountRan.current) {
      hasMountRan.current = true;
      return () => {
        hasMountRan.current = selected;
      };
    }

    setChanged(selected);
    return () => {
      hasMountRan.current = selected;
    };
  }, [selected]);

  function getButtonClasses() {
    return classNames(classes.wrapper, {
      [classes.hover]: hovered || parentHovered,
      [classes.selected]: selected,
    });
  }

  function getHoverProps() {
    if (disableHover) {
      return {};
    }

    return {
      onMouseEnter: () => setHovered(true),
      onMouseLeave: () => setHovered(false),
    };
  }

  function getCheckProps() {
    const checkClasses = classNames(classes.icon, classes.check, {
      [classes.changed]: changed,
    });

    return {
      style: { fontSize: size * FONT_SIZE_RATIO },
      className: checkClasses,
    };
  }

  function getAddProps() {
    const addClasses = classNames(classes.icon, classes.add);

    return {
      style: { fontSize: size * FONT_SIZE_RATIO },
      className: addClasses,
    };
  }

  const buttonProps = {
    onClick,
    style: { height: size, width: size },
    className: getButtonClasses(),
    ...getHoverProps(),
  };

  return (
    <ButtonBase disabled={disabled} component="div" {...buttonProps}>
      {selected ? <Check {...getCheckProps()} /> : <Add {...getAddProps()} />}
    </ButtonBase>
  );
};

ToggleButton.defaultProps = {
  size: 30,
  disabled: false,
  disableHover: false,
  parentHovered: false,
  onClick: () => undefined,
};

export default withStyles(styles)(ToggleButton);
